import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { catalog, ContentType, isFutureDated } from "../api/tmdb";
import DetailHero from "../components/DetailHero";
import PosterRow from "../components/PosterRow";
import { LoadingScreen, ErrorScreen, EmptyScreen } from "../components/StatusScreens";
import { getProgress } from "../storage/progress";

// Server-side source check (see superembed-check.js): the app asks the server
// whether SuperEmbed — the remaining third-party source, since VidLink was
// unwired on 2026-09-24 — has a real file for this title. It distinguishes
// "file is genuinely gone" (dead) from "provider was busy, retry later"
// (transient) so the UI never blames the movie for a rate-limit hiccup.
//
// This is a courtesy signal, not a gate: titles served from the admin-mapped
// hosted package, YouTube, TorBox or a torrent are resolved at play time and
// never consult it, and the reserve tier sits behind SuperEmbed.
//
// The endpoint keeps its /api/vidking/ name because that is the path the
// deployed function and routes already expose; it probes SuperEmbed.
function SourceStatus({ movie, refreshKey, onRecheck }) {
  const [check, setCheck] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCheck(null); // null = checking
    const params = new URLSearchParams({ type: "movie", id: String(movie.tmdbId) });
    if (refreshKey > 0) params.set("refresh", "1");
    fetch(`/api/vidking/superembed-check?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setCheck(d);
      })
      .catch(() => {
        if (!cancelled) setCheck({ available: false, transient: true, error: "network" });
      });
    return () => {
      cancelled = true;
    };
  }, [movie.tmdbId, refreshKey]);

  if (!check) {
    return <p className="source-check checking">Checking source availability…</p>;
  }
  if (check.available) {
    return <p className="source-check ok">✓ Source available</p>;
  }
  if (check.dead) {
    return (
      <p className="source-check bad">
        ⚠ No source available — this movie’s file is not on any provider right now.
        <button className="link-button" onClick={() => onRecheck()}>Recheck</button>
      </p>
    );
  }
  return (
    <p className="source-check warn">
      Couldn’t verify the source right now (provider busy). Playback may still work — try it.
      <button className="link-button" onClick={() => onRecheck()}>Retry check</button>
    </p>
  );
}

export default function MovieDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, movie: null, error: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [sameLang, setSameLang] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setSameLang([]);
    if (!state.movie || !state.movie.originalLanguage) return undefined;
    catalog
      .sameLanguageMovies(state.movie)
      .then((list) => { if (!cancelled) setSameLang(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [state.movie?.tmdbId, state.movie?.originalLanguage]);

  // One "More Like This" row: same-language titles lead (e.g. Malayalam for
  // Mayamohini), then TMDB's own recommendations — deduped by tmdbId.
  const moreLikeThis = (() => {
    if (!state.movie) return [];
    const seen = new Set();
    const out = [];
    for (const c of [...sameLang, ...state.movie.recommendations]) {
      if (!c || seen.has(c.tmdbId)) continue;
      seen.add(c.tmdbId);
      out.push(c);
    }
    return out;
  })();

  const load = useCallback(() => {
    setState({ loading: true, movie: null, error: null });
    setRefreshKey(0);
    catalog
      .movieDetails(Number(id))
      .then((movie) => setState({ loading: false, movie }))
      .catch((error) => setState({ loading: false, movie: null, error: error.message || "Something went wrong. Try again." }));
  }, [id]);

  useEffect(load, [load]);

  const onRecheck = () => setRefreshKey((k) => k + 1);
  if (state.loading) return <LoadingScreen label="Loading…" />;
  if (state.error) return <ErrorScreen message={state.error} onRetry={load} />;
  const movie = state.movie;

  // An unreleased title has no file to watch — show an honest empty state
  // instead of a PLAY button that would just fail at the provider.
  if (isFutureDated(movie.releaseDate)) {
    return (
      <EmptyScreen
        title={movie.title}
        message={`Not released yet${movie.releaseDate ? ` — releases ${movie.releaseDate}` : ""}. It will appear here once it's out.`}
      />);
  }

  const play = () => {
    const saved = getProgress(ContentType.MOVIE, movie.tmdbId);
    navigate(
      `/watch?type=movie&id=${movie.tmdbId}&title=${encodeURIComponent(movie.title)}&poster=${encodeURIComponent(movie.posterPath || "")}&backdrop=${encodeURIComponent(movie.backdropPath || "")}&lang=${encodeURIComponent(movie.originalLanguage || "")}&resume=${saved?.positionSeconds || 0}`,
    );
  };

  return (
    <main className="page">
      <DetailHero
        media={{ ...movie, contentType: ContentType.MOVIE, asCard: movie.asCard }}
        playAction={
          <button className="btn btn-primary" onClick={play}>
            ▶ &nbsp;PLAY
          </button>
        }
      >
        {/* The "Source available" line is NOT rendered while no provider in
            the playback chain exposes a server-side availability probe. It
            used to be driven by VidLink's probe (then by SuperEmbed's); both
            providers were removed from the chain on 2026-09-24, and the
            remaining hosts (the six reserve embeds) have no such endpoint —
            so the line could only report on a provider that no longer plays.
            The SourceStatus component is left defined below (this repo has no
            version control, so it is cheaper to keep than to re-create);
            re-render it here if a chain provider gains a probe. */}
      </DetailHero>
      <div className="page-content">
        {moreLikeThis.length > 0 && (
          <PosterRow
            title="More Like This"
            items={moreLikeThis}
            getProgress={(card) => getProgress(card.contentType, card.tmdbId)}
          />
        )}
      </div>
    </main>
  );
}